import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineError } from "@/components/QueryState";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  type ChannelUserRow,
  type ThreadFlagAccess,
  type ThreadFlagRow,
} from "@/lib/api";
import { Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { channelUserKey, PLATFORM_LABEL } from "./channel-mode";
import { useSaveFlagPolicy } from "./use-flag-policy";

// Copy vocabulary (mirrors channel-mode.ts's register): the category noun is
// "extra"; extras are "turned on/off" ("switch" names only the physical
// control); the in-chat event is an "approval request"; "ask" is what people
// do to Boo. "sign-off"/"mode" don't appear.

const ACCESS_LABEL: Record<ThreadFlagAccess, string> = {
  auto: "Turns it on",
  approval: "Gets an approval first",
  disabled: "Declines",
};

const ACCESS_HINT: Record<ThreadFlagAccess, string> = {
  auto: "Whoever asks gets it right away — and any extra cost starts right away too.",
  approval:
    "Boo posts an approval request in the conversation, in view of everyone there, and waits. Turning it off later needs no approval.",
  disabled: "Boo lets whoever asks know it's turned off for this Boo.",
};

const ACCESS_ORDER: readonly ThreadFlagAccess[] = ["auto", "approval", "disabled"];

// Each card's wording is keyed by what the flag gates. Thread modes are
// something a person asks Boo for; a Boo-wide feature may be something Boo
// does on its own (runtime feedback), so the question, the three answers,
// and the "who approves" lead all come from one copy set.
type FlagCopy = {
  question: string;
  labels: Record<ThreadFlagAccess, string>;
  hints: Record<ThreadFlagAccess, string>;
  approverLead: string;
  /** Rendered under the card when the flag moves data off this Boo. */
  note?: string;
};

const THREAD_MODE_COPY: FlagCopy = {
  question: "When someone asks for it, Boo:",
  labels: ACCESS_LABEL,
  hints: ACCESS_HINT,
  approverLead: "Asking stays open to everyone.",
};

// Boo-wide features without bespoke wording read like extras: someone asks,
// Boo answers per the policy.
const GENERIC_FEATURE_COPY: FlagCopy = {
  question: "When someone asks to use it, Boo:",
  labels: ACCESS_LABEL,
  hints: ACCESS_HINT,
  approverLead: "Asking stays open to everyone.",
};

const FEATURE_COPY: Record<string, FlagCopy> = {
  runtime_feedback: {
    question: "When something goes wrong on its side, Boo:",
    labels: {
      auto: "Sends Boo Lab a report",
      approval: "Asks first",
      disabled: "Keeps it to itself",
    },
    hints: {
      auto: "The report includes that conversation.",
      approval: "Boo asks in the conversation before sending.",
      disabled: "Nothing leaves this Boo.",
    },
    approverLead: "Boo asks in the conversation where it happened.",
  },
};

const copyFor = (flag: ThreadFlagRow): FlagCopy =>
  flag.kind === "feature" ? (FEATURE_COPY[flag.id] ?? GENERIC_FEATURE_COPY) : THREAD_MODE_COPY;

function initialsFor(displayName: string | null, id: string): string {
  if (displayName) {
    const words = displayName.trim().split(/\s+/);
    return words
      .slice(0, 2)
      .map((w) => w[0] ?? "")
      .join("")
      .toUpperCase();
  }
  return id.slice(0, 2).toUpperCase();
}

/**
 * "Extras" tab: the flag policies for one Boo, in two groups. Thread modes
 * are per-conversation abilities people can ask Boo to turn on (deep thinking
 * today); Boo-wide features apply everywhere Boo works (runtime feedback to
 * Boo Lab). The registry is brain-side and grows without dashboard changes.
 * Each card is the single owning surface for one flag's policy — access
 * choice AND the approver list live here, so the current policy is always
 * answerable at a glance and nothing points at a control elsewhere.
 */
export function ThreadModesSection({
  containerId,
  flags,
  users,
  canToggle,
}: {
  containerId: string;
  flags: ThreadFlagRow[];
  users: ChannelUserRow[];
  canToggle: boolean;
}) {
  // A Boo that predates `kind` sends thread modes only.
  const threadModes = flags.filter((f) => f.kind !== "feature");
  const features = flags.filter((f) => f.kind === "feature");
  const both = threadModes.length > 0 && features.length > 0;
  return (
    <div className="flex flex-col gap-8">
      {threadModes.length > 0 && (
        <section className="flex flex-col gap-4">
          {both && <h2 className="text-base font-semibold">For a single conversation</h2>}
          <p className="text-sm text-muted-foreground max-w-prose">
            Extras are things people can ask Boo to turn on for a single
            conversation — deeper, costlier thinking, for example. Decide what Boo
            does with each request. Turning an extra off is different: anyone in
            the conversation can, anytime, no approval needed.
          </p>
          {threadModes.map((flag) => (
            <FlagCard
              key={flag.id}
              containerId={containerId}
              flag={flag}
              users={users}
              canToggle={canToggle}
            />
          ))}
        </section>
      )}
      {features.length > 0 && (
        <section className="flex flex-col gap-4">
          {both && <h2 className="text-base font-semibold">For the whole Boo</h2>}
          <p className="text-sm text-muted-foreground max-w-prose">
            These aren&apos;t tied to one conversation — the choice here applies
            everywhere Boo works.
          </p>
          {features.map((flag) => (
            <FlagCard
              key={flag.id}
              containerId={containerId}
              flag={flag}
              users={users}
              canToggle={canToggle}
            />
          ))}
        </section>
      )}
    </div>
  );
}

function FlagCard({
  containerId,
  flag,
  users,
  canToggle,
}: {
  containerId: string;
  flag: ThreadFlagRow;
  users: ChannelUserRow[];
  canToggle: boolean;
}) {
  // "Specific people" chosen while the server still says "everyone": reveal
  // the picker locally and write nothing until the first person is added — a
  // half-made choice must never publish a no-one-can-approve policy.
  const [picking, setPicking] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const serverList = flag.approvers === "everyone" ? null : (flag.approvers as string[]);
  const listVisible = serverList !== null || picking;

  // Errors roll back and render inside this card.
  const save = useSaveFlagPolicy(containerId);
  const busy = save.isPending;

  const byKey = useMemo(() => {
    const map = new Map<string, ChannelUserRow>();
    for (const u of users) map.set(channelUserKey(u), u);
    return map;
  }, [users]);

  const switchToEveryone = () => {
    setPicking(false);
    if (serverList === null) return;
    if (serverList.length > 0) {
      setConfirmDiscard(true);
      return;
    }
    save.mutate({ flagId: flag.id, access: flag.access, approvers: "everyone" });
  };

  const copy = copyFor(flag);
  // A two-state flag offers no approval option; a stale stored "approval"
  // is what the brain treats as auto, so show it that way.
  const approvable = flag.approvable !== false;
  const options = approvable ? ACCESS_ORDER : ACCESS_ORDER.filter((a) => a !== "approval");
  const shownAccess = !approvable && flag.access === "approval" ? "auto" : flag.access;

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border p-4">
      <div>
        <div className="text-sm font-medium">{flag.title}</div>
        <p className="text-sm text-muted-foreground max-w-prose">{flag.summary}</p>
      </div>

      <div className="flex flex-col gap-2">
        <div id={`${flag.id}-access-label`} className="text-sm text-muted-foreground">
          {copy.question}
        </div>
        <RadioGroup
          value={shownAccess}
          onValueChange={(v) =>
            save.mutate({
              flagId: flag.id,
              access: v as ThreadFlagAccess,
              approvers: flag.approvers,
            })
          }
          disabled={!canToggle || busy}
          aria-labelledby={`${flag.id}-access-label`}
          className="gap-2"
        >
          {options.map((access) => (
            <div key={access} className="flex items-start gap-2">
              <RadioGroupItem
                value={access}
                id={`${flag.id}-${access}`}
                className="mt-0.5"
              />
              <Label
                htmlFor={`${flag.id}-${access}`}
                className="flex flex-col items-start gap-0.5 font-normal"
              >
                <span className="text-sm">{copy.labels[access]}</span>
                <span className="text-xs text-muted-foreground">
                  {copy.hints[access]}
                </span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {approvable && flag.access === "approval" && (
        <div className="flex flex-col gap-3 rounded-md border border-border/60 p-3">
          <div className="text-sm">
            Who can approve?{" "}
            <span className="text-muted-foreground">{copy.approverLead}</span>
          </div>
          <div className="flex gap-1">
            <Button
              variant={listVisible ? "ghost" : "secondary"}
              size="sm"
              aria-pressed={!listVisible}
              disabled={!canToggle || busy}
              onClick={switchToEveryone}
            >
              Anyone in the conversation
            </Button>
            <Button
              variant={listVisible ? "secondary" : "ghost"}
              size="sm"
              aria-pressed={listVisible}
              disabled={!canToggle || busy}
              onClick={() => setPicking(true)}
            >
              Specific people
            </Button>
          </div>
          {listVisible && (
            <ApproverList
              flagTitle={flag.title}
              approvers={serverList ?? []}
              byKey={byKey}
              users={users}
              canToggle={canToggle}
              busy={busy}
              onChange={(approvers) =>
                save.mutate({ flagId: flag.id, access: flag.access, approvers })
              }
            />
          )}
        </div>
      )}

      {copy.note && flag.access !== "disabled" && (
        <p className="text-xs text-muted-foreground max-w-prose">{copy.note}</p>
      )}

      {save.error != null && <InlineError error={save.error} />}

      <ConfirmDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard the approver list?"
        description={`“Anyone in the conversation” means any member's approval counts for ${flag.title}. Your ${serverList?.length ?? 0} picked ${(serverList?.length ?? 0) === 1 ? "person is" : "people are"} discarded — choosing “Specific people” again starts from an empty list.`}
        confirmLabel="Discard and allow anyone"
        loading={busy}
        onConfirm={() => {
          save.mutate({ flagId: flag.id, access: flag.access, approvers: "everyone" });
          setConfirmDiscard(false);
        }}
      />
    </div>
  );
}

function ApproverList({
  flagTitle,
  approvers,
  byKey,
  users,
  canToggle,
  busy,
  onChange,
}: {
  flagTitle: string;
  approvers: string[];
  byKey: Map<string, ChannelUserRow>;
  users: ChannelUserRow[];
  canToggle: boolean;
  busy: boolean;
  onChange: (approvers: string[]) => void;
}) {
  const [search, setSearch] = useState("");
  const candidates = useMemo(() => {
    const selected = new Set(approvers);
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => !selected.has(channelUserKey(u)))
      .filter(
        (u) => !q || `${u.displayName ?? ""} ${u.providerUserId}`.toLowerCase().includes(q),
      )
      .sort((a, b) =>
        (a.displayName ?? a.providerUserId).localeCompare(b.displayName ?? b.providerUserId),
      );
  }, [users, search, approvers]);

  const resolved = approvers.filter((key) => byKey.has(key));
  const unresolved = approvers.filter((key) => !byKey.has(key));

  return (
    <div className="flex flex-col gap-2">
      {approvers.length === 0 && (
        <p className="text-sm text-amber-600 dark:text-amber-500">
          No approvers yet. Requests for {flagTitle} will wait with no one able
          to approve them — add at least one person.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {resolved.map((key) => {
          const user = byKey.get(key)!;
          const name = user.displayName ?? user.providerUserId;
          return (
            <span
              key={key}
              className="flex items-center gap-1.5 rounded-full border border-border py-0.5 pl-1 pr-1.5 text-sm"
            >
              <Avatar className="size-5">
                <AvatarFallback className="text-[9px]">
                  {initialsFor(user.displayName, user.providerUserId)}
                </AvatarFallback>
              </Avatar>
              {name}
              {canToggle && (
                <button
                  aria-label={`Remove approver ${name}`}
                  disabled={busy}
                  onClick={() => onChange(approvers.filter((k) => k !== key))}
                  className="rounded-full p-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              )}
            </span>
          );
        })}
        {canToggle && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={busy}>
              <Button variant="outline" size="sm">
                <Plus className="size-4" />
                Add a person
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <div className="p-1.5">
                {/* stopPropagation: the menu's typeahead would otherwise
                    capture keystrokes and pull focus off the field. */}
                <Input
                  placeholder="Search people…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="h-8"
                />
              </div>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Approval follows a person&apos;s chat identity — their Slack,
                Google Chat, or Teams account, not a dashboard login.
              </DropdownMenuLabel>
              {candidates.length === 0 ? (
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                  {users.length === 0
                    ? "No one to pick yet — people appear after they write to Boo, or add them on the People tab first."
                    : "No one matches."}
                </DropdownMenuLabel>
              ) : (
                <>
                  {candidates.slice(0, 30).map((u) => (
                    <DropdownMenuItem
                      key={channelUserKey(u)}
                      // preventDefault keeps the menu open so several people can
                      // be added in one visit.
                      onSelect={(e) => {
                        e.preventDefault();
                        onChange([...approvers, channelUserKey(u)]);
                      }}
                    >
                      <Avatar className="size-6">
                        <AvatarFallback className="text-[9px]">
                          {initialsFor(u.displayName, u.providerUserId)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-sm">
                          {u.displayName ?? u.providerUserId}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {PLATFORM_LABEL[u.platform]}
                        </span>
                      </span>
                    </DropdownMenuItem>
                  ))}
                  {candidates.length > 30 && (
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                      +{candidates.length - 30} more — keep typing to narrow
                    </DropdownMenuLabel>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {unresolved.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">
            Boo can&apos;t match these approvers to anyone it knows:
          </span>
          {unresolved.map((key) => (
            <span
              key={key}
              className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 font-mono text-xs text-muted-foreground"
            >
              {key}
              {canToggle && (
                <button
                  aria-label={`Remove approver ${key}`}
                  disabled={busy}
                  onClick={() => onChange(approvers.filter((k) => k !== key))}
                  className="rounded-full p-0.5 hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
